import type { ColumnDef } from '@tanstack/react-table';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { canWrite } from 'app/access';
import { useAppSelector } from 'app/store';
import { selectUserInfo } from 'app/store/user';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Badge, type BadgeProps } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { DataTable } from 'components/ui/data-table';
import { Typography } from 'components/ui/typography';
import { Routes } from 'config/constants';
import { formatPrice } from 'utils/formatters';

import {
  type CommissionInvoice,
  type CommissionInvoiceStatus,
  useApproveCommissionInvoice,
  useCommissionInvoices,
  useLatestCommissionInvoiceStatus,
} from '../hooks/useCommissionInvoices';

const APPROVAL_ERROR_FALLBACK = "Couldn't approve this invoice. Try again.";
const APPROVAL_OUTCOME_UNKNOWN =
  "We couldn't confirm the approval. Check the invoice status before trying again.";

// Shown instead of an error when the refetch after a failed approve finds the money already moving.
const MONEY_MOVING_MESSAGE: Partial<Record<CommissionInvoiceStatus, string>> = {
  processing: 'Payment is processing',
  paid: 'This invoice is paid',
};

const STATUS_LABEL = {
  awaiting_approval: 'Awaiting approval',
  processing: 'Processing',
  payment_failed: 'Payment failed',
  paid: 'Paid',
  void: 'Void',
  uncollectible: 'Uncollectible',
} as const satisfies Record<CommissionInvoiceStatus, string>;

const STATUS_VARIANT = {
  awaiting_approval: 'warn',
  processing: 'info',
  payment_failed: 'error',
  paid: 'success',
  void: 'neutral',
  uncollectible: 'error',
} as const satisfies Record<CommissionInvoiceStatus, NonNullable<BadgeProps['variant']>>;

// The statuses a developer can act on, and what the action is called for each.
const ACTION_LABEL: Partial<Record<CommissionInvoiceStatus, string>> = {
  awaiting_approval: 'Approve',
  payment_failed: 'Retry payment',
};

const actionLabel = (invoice: CommissionInvoice) => ACTION_LABEL[invoice.status];

const formatMoney = (amount: CommissionInvoice['amount']) =>
  formatPrice(amount.amountSubunits / 100, amount.currencyCode);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatPeriod = (invoice: CommissionInvoice) =>
  `${formatDate(invoice.periodStart)} to ${formatDate(invoice.periodEnd)}`;

const commissionLinkLabel = (count: number) =>
  `${count} ${count === 1 ? 'commission' : 'commissions'}`;

const serverMessage = (error: unknown) => {
  if (typeof error !== 'object' || error === null || !('error' in error)) {
    return undefined;
  }

  const body = error.error;
  if (typeof body !== 'object' || body === null || !('message' in body)) {
    return undefined;
  }

  if (typeof body.message !== 'string' || body.message.length === 0) {
    return undefined;
  }

  return body.message;
};

const approvalErrorMessage = (error: unknown) => serverMessage(error) ?? APPROVAL_ERROR_FALLBACK;

const responseStatus = (error: unknown) => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return undefined;
  }

  return typeof error.status === 'number' ? error.status : undefined;
};

// 404 and 409 mean the server already refused this draft. Confirm must not post it again.
const invoiceCanNoLongerBeApproved = (error: unknown) => {
  const status = responseStatus(error);
  return status === 404 || status === 409;
};

// A 5xx or a dropped connection can arrive after the server has started the debit.
const approvalOutcomeIsUnknown = (error: unknown) => {
  const status = responseStatus(error);
  return status === undefined || status >= 500;
};

const approveColumn = (onApprove: (invoiceId: string) => void): ColumnDef<CommissionInvoice> => ({
  id: 'approve',
  header: '',
  meta: { cellClassName: 'whitespace-nowrap' },
  cell: ({ row }) => {
    const label = actionLabel(row.original);
    if (!label) {
      return null;
    }

    return (
      <Button type="button" variant="outline" size="sm" onClick={() => onApprove(row.original.id)}>
        {label}
      </Button>
    );
  },
});

const commissionInvoiceColumns = (
  canApprove: boolean,
  onApprove: (invoiceId: string) => void,
): ColumnDef<CommissionInvoice>[] => [
  {
    id: 'period',
    header: 'Period',
    cell: ({ row }) => formatPeriod(row.original),
  },
  {
    id: 'commissions',
    header: 'Commissions',
    cell: ({ row }) => {
      const invoice = row.original;
      // Voiding releases the commissions, so this filter would open an empty list.
      if (invoice.status === 'void') {
        return <span className="tabular-nums">{invoice.commissionCount}</span>;
      }

      return (
        <Link
          to={`${Routes.Commissions}?invoiceId=${encodeURIComponent(invoice.id)}`}
          aria-label={commissionLinkLabel(invoice.commissionCount)}
          className="tabular-nums underline underline-offset-4"
        >
          {invoice.commissionCount}
        </Link>
      );
    },
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) => <span className="tabular-nums">{formatMoney(row.original.amount)}</span>,
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      return <Badge variant={STATUS_VARIANT[status]}>{STATUS_LABEL[status]}</Badge>;
    },
  },
  ...(canApprove ? [approveColumn(onApprove)] : []),
];

const CommissionInvoicesShell = ({ children }: { children: ReactNode }) => (
  <Card className="mt-6">{children}</Card>
);

const CommissionInvoicesLoading = () => (
  <CommissionInvoicesShell>
    <CardContent className="p-6">
      <div
        className="h-48 animate-pulse rounded bg-muted"
        role="status"
        aria-label="Loading commission invoices"
      />
    </CardContent>
  </CommissionInvoicesShell>
);

const CommissionInvoicesError = () => (
  <CommissionInvoicesShell>
    <CardContent className="p-6">
      <Typography variant="small" as="p">
        Unable to load commission invoices. Please refresh the page to try again.
      </Typography>
    </CardContent>
  </CommissionInvoicesShell>
);

const CommissionInvoicesTable = ({
  invoices,
  canApprove,
}: {
  invoices: CommissionInvoice[];
  canApprove: boolean;
}) => {
  // The clicked row goes stale after a refetch. Follow the id in the current list instead.
  const [invoiceIdToApprove, setInvoiceIdToApprove] = useState<string | null>(null);
  const approveInvoice = useApproveCommissionInvoice();
  const latestStatus = useLatestCommissionInvoiceStatus();
  const selectedInvoice = invoices.find((invoice) => invoice.id === invoiceIdToApprove) ?? null;
  const invoiceToApprove =
    selectedInvoice !== null && actionLabel(selectedInvoice) !== undefined ? selectedInvoice : null;
  if (invoiceIdToApprove !== null && invoiceToApprove === null) {
    setInvoiceIdToApprove(null);
  }
  const isSubmitting = approveInvoice.isPending;
  const showApprove = canApprove && invoices.some((invoice) => actionLabel(invoice) !== undefined);
  const isRetry = invoiceToApprove?.status === 'payment_failed';

  const confirmApproval = async () => {
    if (!invoiceToApprove) {
      return;
    }

    const invoiceId = invoiceToApprove.id;
    try {
      await approveInvoice.mutateAsync(invoiceId);
      setInvoiceIdToApprove(null);
      toast.success(isRetry ? 'Payment retried' : 'Invoice approved', {
        id: 'commission-invoice-approved',
      });
    } catch (error) {
      // The mutation's refetch has finished by now, so the list shows whether the debit started.
      const status = latestStatus(invoiceId);
      const moneyMovingMessage = status === undefined ? undefined : MONEY_MOVING_MESSAGE[status];
      if (moneyMovingMessage !== undefined) {
        setInvoiceIdToApprove(null);
        toast.info(moneyMovingMessage, { id: 'commission-invoice-approve-error' });
        return;
      }

      // Closing sends the developer back to the refreshed status the message asks them to check.
      if (approvalOutcomeIsUnknown(error)) {
        setInvoiceIdToApprove(null);
        toast.error(APPROVAL_OUTCOME_UNKNOWN, { id: 'commission-invoice-approve-error' });
        return;
      }

      toast.error(approvalErrorMessage(error), { id: 'commission-invoice-approve-error' });
      if (invoiceCanNoLongerBeApproved(error)) {
        setInvoiceIdToApprove(null);
      }
    }
  };

  return (
    <CommissionInvoicesShell>
      <CardHeader>
        <CardTitle>Commission invoices</CardTitle>
        <CardDescription>
          Approve an invoice to debit your linked bank account by ACH.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={commissionInvoiceColumns(showApprove, setInvoiceIdToApprove)}
          data={invoices}
          hideToolbar
        />
        <AlertDialog
          open={invoiceToApprove !== null}
          onOpenChange={(open) => {
            if (!isSubmitting && !open) {
              setInvoiceIdToApprove(null);
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isRetry ? 'Retry commission payment' : 'Approve commission invoice'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {invoiceToApprove
                  ? `We'll debit ${formatMoney(
                      invoiceToApprove.amount,
                    )} from your linked bank account by ACH. It usually takes about 4 business days to clear.`
                  : null}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              {!isSubmitting && <AlertDialogCancel>Cancel</AlertDialogCancel>}
              <AlertDialogAction
                onClick={(event) => {
                  event.preventDefault();
                  void confirmApproval();
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Approving...' : 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </CommissionInvoicesShell>
  );
};

export function CommissionInvoicesCard() {
  const userInfo = useAppSelector(selectUserInfo);
  const canApprove = canWrite(userInfo, 'billing');
  const { invoices, isLoading, error } = useCommissionInvoices();

  if (isLoading) {
    return <CommissionInvoicesLoading />;
  }

  if (error) {
    return <CommissionInvoicesError />;
  }

  if (invoices.length === 0) {
    return null;
  }

  return <CommissionInvoicesTable invoices={invoices} canApprove={canApprove} />;
}
